import { Handshake, FileSearch, Gavel, ShieldCheck } from "lucide-react";

const CONSULTATION = "./images/consultation.jpeg";
const OFFICE_INTERIOR = "./images/office-interior.jpeg";

const pillars = [
  {
    icon: Handshake,
    title: "Client-First Counsel",
    desc: "Every matter begins with attentive listening. We map your goals, your risks, and your priorities before a single filing is drafted.",
  },
  {
    icon: FileSearch,
    title: "Meticulous Preparation",
    desc: "Cases are won in the research. We build fact-tight, precedent-backed arguments long before we step into a courtroom.",
  },
  {
    icon: Gavel,
    title: "Courtroom Craft",
    desc: "Appearances across District, Sessions and High Courts in Kerala &mdash; with a composed, strategic advocacy style.",
  },
  {
    icon: ShieldCheck,
    title: "Absolute Confidentiality",
    desc: "Your conversations and documents are protected by privilege &mdash; and by a practice that treats discretion as non-negotiable.",
  },
];

export default function ApproachSection() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="py-28 bg-[#f0e8d8] relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#b89968]/10 rounded-full blur-3xl -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-[#b89968]/10 border border-[#b89968]/20">
            <Handshake className="w-3.5 h-3.5 gold-accent" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase gold-accent">
              Our Approach
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-[#0f1d3a] mb-3">
            How We <span className="italic font-normal gold-accent">Work With You</span>
          </h2>
          <div className="section-gold-line mx-auto mb-6" />
          <p className="text-base leading-relaxed text-[#6a7690] max-w-2xl mx-auto">
            Four principles shape every brief we take, every argument we write, and every
            outcome we pursue at Corpus Juris Law Chambers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Pillars */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pillars.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`approach-${title.toLowerCase().replace(/\s+/g, "-")}`}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                className="spotlight-card shimmer-border relative p-7 rounded-2xl bg-white border border-[#d9cdb4] shadow-sm hover:shadow-xl hover:shadow-[#b89968]/10 hover:-translate-y-1 hover:border-[#b89968]/40 transition-all duration-300 group overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-[#b89968]/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="w-12 h-12 mb-5 rounded-xl bg-[#0f1d3a] group-hover:bg-[#b89968] flex items-center justify-center transition-colors duration-300">
                    <Icon className="w-5 h-5 text-white group-hover:text-[#0f1d3a] transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#0f1d3a] mb-2 tracking-tight">
                    {title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed text-[#6a7690]"
                    dangerouslySetInnerHTML={{ __html: desc }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Stacked photo collage */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-4 -right-4 w-28 h-28 border-2 border-[#b89968]/40 rounded-2xl hidden md:block" />
            {/* Primary */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-[#0f1d3a]/15 ring-1 ring-[#b89968]/20 group">
              <img
                src={CONSULTATION}
                alt="Consultation at Corpus Juris Law Chambers"
                className="w-full h-[420px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                data-testid="approach-photo-primary"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 pr-44 bg-gradient-to-t from-[#0f1d3a]/80 to-transparent">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#b89968] font-bold mb-1">
                  Consultation
                </p>
                <p className="text-sm text-white/85 leading-snug">
                  Unhurried. Confidential.
                </p>
              </div>
            </div>

            {/* Secondary thumbnail */}
            <div className="absolute -bottom-8 -right-8 w-48 h-36 rounded-xl overflow-hidden shadow-xl ring-2 ring-[#f7f3ea] hidden md:block group">
              <img
                src={OFFICE_INTERIOR}
                alt="The Corpus Juris team"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                data-testid="approach-photo-secondary"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
